/**
 * Typed event taxonomy for product analytics.
 *
 * All track call sites in the app should import `track()` from here so the
 * event name + payload shape are enforced at compile time. Adding a new
 * event = add it to `EventMap`, then call `track("name", {...})`.
 *
 * No PII allowed in payloads — UUIDs and enum-ish values only.
 *
 * Forwards to Plausible Analytics (Business plan). `purchase` is routed
 * through the revenue helper so Plausible's Ecommerce revenue charts
 * pick it up when the `Purchase` goal is marked as a revenue goal in
 * the dashboard. All other events fire as plain custom events.
 */

import { trackEventPlausible, trackRevenue } from "./plausible";

export type EventMap = {
  // Signup / auth
  signup_started: { tier?: string; billing?: "monthly" | "yearly" };
  signup_card_captured: { tier: string; billing: "monthly" | "yearly" };
  signup_completed: { tier: string };
  magic_link_sent: { reason: "signin" | "signup_finalize" };

  // Checkout
  checkout_opened: { priceId: string; context: "pricing" | "signup" | "upgrade" };
  checkout_succeeded: { priceId: string };
  checkout_failed: { priceId: string; reason?: string };

  // Tier changes
  tier_upgraded: { from: string; to: string };
  tier_downgraded: { from: string; to: string };

  // Revenue (Plausible Business — marked as revenue goal in dashboard).
  purchase: {
    amount: number;
    currency: string;
    priceId: string;
    tier?: string;
    billing?: "monthly" | "yearly";
  };

  // Content
  excerpt_created: { sourceLinked: boolean };
  piece_published: { destination: string };
  

  // Integrations
  readwise_synced: { highlights: number };
  feed_item_saved: { promoted_to_library: boolean };
};

// Plausible's event name for revenue is conventionally capitalized
// ("Purchase") to match how it appears in the dashboard.
const REVENUE_EVENT_NAME = "Purchase";

const sanitizeProps = (payload: Record<string, unknown>): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") out[k] = v;
  }
  return out;
};

export const track = <K extends keyof EventMap>(name: K, payload: EventMap[K]): void => {
  if (name === "purchase") {
    const p = payload as EventMap["purchase"];
    const { amount, currency, ...rest } = p;
    trackRevenue(REVENUE_EVENT_NAME, { amount, currency, ...sanitizeProps(rest) });
    return;
  }
  trackEventPlausible(name as string, sanitizeProps(payload as Record<string, unknown>));
};
